import * as Api from '@ViewModels';
import * as React from 'react';
import { toast } from 'sonner';
import { useGetGoogleReviewAutoReplySettings, usePutGoogleReviewAutoReplySettings } from '../../../../queries';
import { autoReplySettingsEqual } from './constants';

/**
 * Shared staged-edits/save lifecycle for the account-wide auto-reply setting, used by both the
 * satisfaction survey editor and the integration-page card so their behavior cannot drift.
 */
export const useGoogleReviewAutoReplySettingsForm = ({
	enabled,
	impersonationContext,
}: {
	enabled: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) => {
	const settingsQuery = useGetGoogleReviewAutoReplySettings({
		enabled,
		impersonationContext,
		meta: { toastErrorMessage: 'Failed to load AI auto-reply settings' },
	});
	const [pendingSettings, setPendingSettings] = React.useState<Api.IGoogleReviewAutoReplySettings | null>(null);
	const putSettingsMutation = usePutGoogleReviewAutoReplySettings({
		impersonationContext,
		onError: () => toast.error('Failed to save AI auto-reply settings'),
		onSuccess: (_saved, submitted) => {
			// Only clear the staged edits when they are still the snapshot that was saved; edits made
			// while the request was in flight stay staged instead of being silently dropped.
			setPendingSettings(current => (current === submitted ? null : current));
			toast.success('AI auto-reply settings saved');
		},
	});

	const hasUnsavedSettings = pendingSettings != null && !autoReplySettingsEqual(pendingSettings, settingsQuery.data);
	const saveSettings = () => {
		if (pendingSettings && hasUnsavedSettings && !putSettingsMutation.isPending) {
			putSettingsMutation.mutate(pendingSettings);
		}
	};

	return {
		hasUnsavedSettings,
		isLoaded: settingsQuery.isSuccess,
		isSaving: putSettingsMutation.isPending,
		saveSettings,
		setPendingSettings,
		settings: pendingSettings ?? settingsQuery.data,
	};
};
